/**
 * ViewAsMenu — owner/admin "view this case as any seat" for troubleshooting.
 *
 * Opens a case in each seat's real shell in a new tab. The owner role clears every
 * server-side require_role gate and ProtectedAttorneyRoute admits company staff, so the
 * internal-seat deep-links render as that seat would see them. "Client portal" fetches
 * the case's EXISTING client magic-link (admin endpoint, read-only) and opens exactly
 * what the customer sees — the only way to troubleshoot the client experience.
 *
 * `startOpen` is for the harness (render the menu expanded for a screenshot).
 */

import React, { useState } from 'react';
import { Eye, ChevronDown, ExternalLink, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/TestAuthContext';
import { api } from '../../utils/api';

const SEATS: Array<{ label: string; href: (id: string) => string }> = [
  { label: 'Attorney', href: (id) => `/attorney/case/${id}/intake` },
  { label: 'Accountant', href: (id) => `/case/${id}/intake` },
  { label: 'Adjuster', href: (id) => `/adjuster/case/${id}/analysis` },
  { label: 'Data specialist', href: (id) => `/data-specialist/case/${id}/documents` },
];

export default function ViewAsMenu({ caseId, startOpen = false }: { caseId: string; startOpen?: boolean }) {
  const { getToken } = useAuth();
  const [open, setOpen] = useState(startOpen);
  const [clientMsg, setClientMsg] = useState<string | null>(null);
  const [loadingClient, setLoadingClient] = useState(false);

  const item =
    'flex w-full items-center justify-between gap-3 px-3 py-2 text-sm text-left transition-colors hover:bg-[var(--color-bg-surface)]';

  const openSeat = (href: string) => {
    window.open(href, '_blank', 'noopener');
    setOpen(false);
  };

  const openClient = async () => {
    setLoadingClient(true);
    setClientMsg(null);
    try {
      const token = await getToken();
      const data = await api.get<{ is_initialized: boolean; magic_link_url?: string }>(
        `/api/v1/client-requests/admin/case/${caseId}/portal-link`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (data?.is_initialized && data.magic_link_url) {
        window.open(data.magic_link_url, '_blank', 'noopener');
        setOpen(false);
      } else {
        setClientMsg('No client link yet — none has been sent for this case.');
      }
    } catch (e: any) {
      setClientMsg(e?.message || 'Could not fetch the client link.');
    } finally {
      setLoadingClient(false);
    }
  };

  return (
    <div className="relative inline-block text-left" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-lg border border-transparent px-3 py-1.5 text-sm transition-colors hover:border-[var(--color-border)] hover:text-[var(--color-text-primary)]"
        style={{ color: 'var(--color-text-muted)' }}
        title="View this case as any seat"
      >
        <Eye className="w-4 h-4" />
        View as
        <ChevronDown className="w-3.5 h-3.5" />
      </button>

      {open && (
        <div
          className="absolute right-0 z-20 mt-1 w-60 rounded-lg border shadow-lg overflow-hidden"
          style={{ background: 'var(--color-bg-elevated)', borderColor: 'var(--color-border)' }}
        >
          {SEATS.map((s) => (
            <button key={s.label} onClick={() => openSeat(s.href(caseId))} className={item}
              style={{ color: 'var(--color-text-primary)' }}>
              <span>{s.label}</span>
              <ExternalLink className="w-3.5 h-3.5" style={{ color: 'var(--color-text-dim)' }} />
            </button>
          ))}

          <div className="border-t" style={{ borderColor: 'var(--color-border)' }} />

          <button onClick={openClient} disabled={loadingClient} className={item} style={{ color: 'var(--color-needs-human-text)' }}>
            <span>Client portal (as customer)</span>
            {loadingClient ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
          </button>

          {clientMsg && (
            <p className="px-3 py-2 text-xs border-t" style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-muted)' }}>
              {clientMsg}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
